import type { BadgeProps } from '@fluentui/react-components';
import type { PeopleImportStatus, RequestStatus } from '@lg/shared';

export const usd = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export const shortDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

export const dateTime = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

export const STATUS_LABEL: Record<RequestStatus, string> = {
  Draft: 'Draft',
  Submitted: 'Submitted',
  InReview: 'In review',
  Approved: 'Approved',
  Provisioning: 'Provisioning',
  NeedsAttention: 'Needs attention',
  Validating: 'Validating',
  Ready: 'Ready',
  Cancelled: 'Cancelled',
};

export const STATUS_COLOR: Record<RequestStatus, BadgeProps['color']> = {
  Draft: 'informative',
  Submitted: 'brand',
  InReview: 'warning',
  Approved: 'success',
  Provisioning: 'brand',
  NeedsAttention: 'danger',
  Validating: 'brand',
  Ready: 'success',
  Cancelled: 'subtle',
};

export const PEOPLE_COLOR: Record<PeopleImportStatus, BadgeProps['color']> = {
  Draft: 'informative',
  Invalid: 'danger',
  Valid: 'success',
  Applying: 'brand',
  Applied: 'success',
  Failed: 'danger',
};

export const DOC_LABEL: Record<string, string> = {
  PurchaseRequisition: 'Purchase requisition',
  PurchaseOrder: 'Purchase order',
  VendorInvoice: 'Vendor invoice',
  Journal: 'Journal',
};

export interface SavedFile {
  fileName: string;
  mimeType: string;
  bytes: Uint8Array;
}

/** Fired after every download while the app runs inside another page (D365 tile); see DownloadNotice. */
export const EMBEDDED_DOWNLOAD_EVENT = 'lg:embedded-download';

/** True inside an iframe (the D365 Website tile); a cross-origin parent makes `top` unreadable. */
export function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export function saveFile(file: SavedFile): void {
  const blob = new Blob([file.bytes as BlobPart], { type: file.mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  // A sandboxed host frame can block downloads silently, with no error the page can see. Embedded,
  // every download also offers to save the same file from a window of its own.
  if (isEmbedded())
    window.dispatchEvent(new CustomEvent<SavedFile>(EMBEDDED_DOWNLOAD_EVENT, { detail: file }));
}

/**
 * Saves a file from a new top-level window (public/download.html), which no host frame sandbox or
 * storage partition applies to. The bytes go over postMessage, same origin only. False when the
 * window could not be opened.
 */
export function saveFileInNewWindow(file: SavedFile): boolean {
  // From the site root (Vite base): routes are paths, so a relative URL would land on /requests/….
  const target = new URL(`${import.meta.env.BASE_URL}download.html`, window.location.origin);
  const win = window.open(target.href, '_blank');
  if (!win) return false;
  const onMessage = (e: MessageEvent) => {
    if (e.source !== win || e.origin !== target.origin || e.data !== 'lg:download-ready') return;
    window.removeEventListener('message', onMessage);
    win.postMessage({ type: 'lg:file', ...file }, target.origin);
  };
  window.addEventListener('message', onMessage);
  setTimeout(() => window.removeEventListener('message', onMessage), 60_000);
  return true;
}

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong';
}
